import { getApiClient } from '@commander/auth';
import { useTheme } from '@commander/ui';
import { useUserDetails } from '@commander/user-details';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { updateUserThemeSetting } from './api';
import type { SimpleThemeMode, ThemeContextValue, ThemeMode } from './types';
import {
  apiToSimpleTheme,
  applyThemeToDocument,
  getThemeModeFromUserSettings,
  saveThemeToCookie,
  simpleToApiTheme,
  useSystemTheme,
} from './utils';

/**
 * React Context for theme management with user settings synchronization
 */
const ThemeChangerContext = createContext<ThemeContextValue | null>(null);

/**
 * Props for ThemeChangerProvider
 */
interface ThemeChangerProviderProps {
  children: ReactNode;
}

/**
 * Provider component that manages theme state and synchronizes with user settings
 *
 * Features:
 * - Integrates with existing UserDetails context for user preferences
 * - Synchronizes theme changes with backend API
 * - Maintains compatibility with existing ThemeProvider
 * - Handles system theme detection and changes
 * - Provides loading and error states
 *
 * @example
 * ```tsx
 * <ThemeChangerProvider>
 *   <App />
 * </ThemeChangerProvider>
 * ```
 */
export function ThemeChangerProvider({ children }: ThemeChangerProviderProps) {
  const { userDetails, isError, refetch } = useUserDetails();
  const { setTheme: setUiTheme } = useTheme();

  // Get user's theme preference from settings with fallback when API fails
  const userThemeMode = getThemeModeFromUserSettings(
    isError || !userDetails ? null : (userDetails?.user_setting?.appearance ?? null)
  );

  // Resolve actual theme (handles system mode)
  const resolvedTheme = useSystemTheme(userThemeMode);

  // Loading and error states for theme operations
  const [isThemeLoading, setIsThemeLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Apply theme to document whenever resolved theme changes
  useEffect(() => {
    applyThemeToDocument(resolvedTheme);
  }, [resolvedTheme]);

  // Sync with existing ThemeProvider
  useEffect(() => {
    const uiTheme = resolvedTheme === 'dark' ? 'dark' : 'light';
    setUiTheme(uiTheme);
  }, [resolvedTheme, setUiTheme]);

  // Update user theme preference via API
  const setThemeMode = async (mode: ThemeMode): Promise<void> => {
    setIsThemeLoading(true);
    setError(null);

    try {
      // Update backend first if available and mode is not SYSTEM
      const apiClient = getApiClient();
      if (apiClient) {
        await updateUserThemeSetting(apiClient, mode);
        saveThemeToCookie(mode);
        await refetch();
      } else {
        // Fallback to cookie when API is not available
        saveThemeToCookie(mode);
        console.log('[ThemeChanger] API client not available, saved theme to cookie:', mode);
      }
    } catch (err) {
      // If API fails, save to cookie as fallback
      saveThemeToCookie(mode);
      console.log('[ThemeChanger] API update failed, saved theme to cookie as fallback:', mode);

      const error =
        err instanceof Error ? err : new Error('Failed to update theme via API, saved locally');
      setError(error);
      console.error('ThemeChanger: Failed to update theme mode via API:', error);
      // Don't throw error since we saved to localStorage successfully
    } finally {
      setIsThemeLoading(false);
    }
  };

  // Simple theme switching function for light/dark/system
  const setSimpleTheme = async (simpleMode: SimpleThemeMode): Promise<void> => {
    const apiMode = simpleToApiTheme(simpleMode);
    await setThemeMode(apiMode);
  };

  const value: ThemeContextValue = {
    currentTheme: resolvedTheme,
    themeMode: userThemeMode,
    simpleThemeMode: apiToSimpleTheme(userThemeMode),
    setThemeMode,
    setSimpleTheme,
    isLoading: isThemeLoading,
    error,
  };

  return <ThemeChangerContext.Provider value={value}>{children}</ThemeChangerContext.Provider>;
}

/**
 * Hook to access theme changer context
 *
 * @throws Error if used outside ThemeChangerProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { currentTheme, simpleThemeMode, setSimpleTheme, isLoading } = useThemeChanger();
 *
 *   const handleThemeToggle = () => {
 *     setSimpleTheme(simpleThemeMode === 'light' ? 'dark' : 'light');
 *   };
 *
 *   const handleSystemTheme = () => {
 *     setSimpleTheme('system');
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleThemeToggle} disabled={isLoading}>
 *         Current theme: {currentTheme}
 *       </button>
 *       <button onClick={handleSystemTheme}>
 *         Use System Theme
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useThemeChanger(): ThemeContextValue {
  const context = useContext(ThemeChangerContext);

  if (!context) {
    throw new Error(
      'useThemeChanger must be used within a ThemeChangerProvider. ' +
        'Make sure your component tree is wrapped with <ThemeChangerProvider>.'
    );
  }

  return context;
}
